/**
 * User API — real backend calls.
 *
 * See BACKEND_CONTRACT.md for endpoint specifications.
 */

import axiosInstance from './axiosInstance';

/**
 * Submit signup form (called when isNewUser=true after OTP verify).
 *
 * BACKEND: POST /user/signup
 * AUTH:    Bearer access token required
 * BODY:    { firstName, lastName, age, gender, address, chronicDiseases[], drugAllergies[], foodAllergies[], language }
 * SUCCESS: { data: { user: {...} } }
 *
 * @param {object} payload - all signup fields
 * @returns {Promise<object>} the created user
 */
export async function signup(payload) {
  const res = await axiosInstance.post('/user/signup', payload);
  return res.data.data.user;
}

/**
 * Fetch the current user's profile.
 *
 * BACKEND: GET /user/profile
 * SUCCESS: { data: { user: {...} } }
 *
 * @returns {Promise<object>}
 */
export async function getProfile() {
  const res = await axiosInstance.get('/user/profile');
  return res.data.data.user;
}

/**
 * Update profile (partial update).
 *
 * BACKEND: PATCH /user/profile
 * BODY:    any subset of signup fields
 * SUCCESS: { data: { user: {...} } }
 *
 * @param {object} updates
 * @returns {Promise<object>}
 */
export async function updateProfile(updates) {
  const res = await axiosInstance.patch('/user/profile', updates);
  return res.data.data.user;
}

/**
 * Get the signed-in client's own analysis report.
 *
 * BACKEND: GET /user/face-analyses/:id/report
 * SUCCESS: { data: { status:'ready', html, summary, generatedAt } | { status:'processing' } }
 *
 * @param {string} analysisId
 * @returns {Promise<object>} the report payload (status + html when ready)
 */
export async function getReport(analysisId) {
  const res = await axiosInstance.get(`/user/face-analyses/${encodeURIComponent(analysisId)}/report`);
  return res.data.data;
}

/**
 * The client's available analysis credits, grouped by clinic + agent.
 *
 * BACKEND: GET /user/agent-credits
 * SUCCESS: { data: { credits: [ { agentKey, agentName, clinicCode, clinicName,
 *                                 available, purchasedAt, firstPurchasedAt } ] } }
 *
 * @returns {Promise<Array>} grouped credit rows (one per clinic-agent pair)
 */
export async function myNotes() {
  const res = await axiosInstance.get('/user/notes');
  return res.data?.data ?? res.data;
}

export async function getCredits() {
  const res = await axiosInstance.get('/user/agent-credits');
  return res.data.data.credits;
}

/** GET /user/physician-access — who has asked for, or holds, my record. */
export async function listPhysicianAccess() {
  const res = await axiosInstance.get('/user/physician-access');
  return res.data.data.grants;
}

/** POST /user/physician-access/:phone — let this doctor see my record. */
export async function grantPhysicianAccess(physicianPhone) {
  const res = await axiosInstance.post(
    `/user/physician-access/${encodeURIComponent(physicianPhone)}`);
  return res.data.data;
}

/** DELETE /user/physician-access/:phone — withdraw it again. */
export async function revokePhysicianAccess(physicianPhone) {
  const res = await axiosInstance.delete(
    `/user/physician-access/${encodeURIComponent(physicianPhone)}`);
  return res.data.data;
}

export const userApi = { listPhysicianAccess, grantPhysicianAccess, revokePhysicianAccess, signup, getProfile, updateProfile, getReport, myNotes, getCredits, setConsent, getSharedClinics, grantClinic, revokeClinic };
export default userApi;

/**
 * Update the client's data-sharing consent preference.
 * BACKEND: PUT /user/consent  BODY: { dataConsent }
 * @param {'clinic_only'|'scie_clinics'|'scie_clinics_ads'} dataConsent
 * @returns {Promise<object>} updated user
 */
export async function setConsent(dataConsent) {
  const res = await axiosInstance.put('/user/consent', { dataConsent });
  return res.data.data.user;
}

/** GET /user/shared-clinics -> [{ code, name, grantedAt }] */
export async function getSharedClinics() {
  const res = await axiosInstance.get('/user/shared-clinics');
  return res.data.data.clinics;
}
/** POST /user/shared-clinics { clinicCode } -> { clinic } */
export async function grantClinic(clinicCode) {
  const res = await axiosInstance.post('/user/shared-clinics', { clinicCode });
  return res.data.data.clinic;
}
/** DELETE /user/shared-clinics/:code */
export async function revokeClinic(code) {
  const res = await axiosInstance.delete(`/user/shared-clinics/${encodeURIComponent(code)}`);
  return res.data.data;
}
